import { useContext, useMemo } from 'react';

import BinanceIcon from '../../../../../assets/icons/binance-icon.svg';
import CoincallIcon from '../../../../../assets/icons/coincall-icon.svg';
import BullishLightIcon from '../../../../../assets/icons/bullish-light-icon.svg';
import BybitIcon from '../../../../../assets/bybit-dark-logo.svg';
import DeribitIcon from '../../../../../assets/icons/deribit-icon.svg';
import OkxIcon from '../../../../../assets/icons/okx-icon.svg';
import OtcIcon from '../../../../../assets/icons/otc-icon.svg';
import { PortfolioManagementContext } from '../../../../../providers/portfolio-management';
import { VenueContext } from '../../../../../providers/venue-provider';
import {
  ExchangeAccountList,
  AlphabeticalAccountList,
  OTCAccountList,
} from '../../../../shared/per-account-list';

type PortfolioListPerAccountProps = {
  isPortfolioSidebarOpen: boolean;
};

export const PortfolioListPerAccount = ({
  isPortfolioSidebarOpen,
}: PortfolioListPerAccountProps) => {
  const {
    setSelectedAccountOption,
    selectedAccountOption,
    getDataDetailsAccount,
    riskSettingsSelected,
    generalSettingsSelected,
    setSpotOverrideValue,
    isNamesBlurred,
  } = useContext(PortfolioManagementContext);
  const { credentials } = useContext(VenueContext);

  const isDisabledAccounts =
    riskSettingsSelected.volShocks.length === 0 ||
    riskSettingsSelected.times.length === 0 ||
    riskSettingsSelected.riskMetrics.length === 0;

  const handleOptionChange = (option: any) => {
    setSpotOverrideValue('');
    setSelectedAccountOption(option);
  };

  const calculatedAccountNames = useMemo(() => {
    return new Set(
      credentials
        .flatMap((cred) => cred.items)
        .filter((item) => {
          const det = getDataDetailsAccount(item);
          return det?.isCalculated;
        })
        .map((item) => item.name),
    );
  }, [credentials, getDataDetailsAccount]);

  const allAccounts = useMemo(
    () => credentials.flatMap((credential) => credential.items),
    [credentials],
  );

  const accountsByExchange = useMemo(() => {
    return allAccounts.reduce<Record<string, any[]>>((acc, item) => {
      const key = item.exchange ?? 'unknown';
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {});
  }, [allAccounts]);

  type ExchangeDefinition = {
    id: string;
    label: string;
    icon: any;
    component: 'exchange' | 'otc';
  };

  const exchangeDefinitions: ExchangeDefinition[] = useMemo(
    () => [
      { id: 'binance', label: 'Binance', icon: BinanceIcon, component: 'exchange' },
      { id: 'bullish', label: 'Bullish', icon: BullishLightIcon, component: 'exchange' },
      { id: 'bybit', label: 'Bybit', icon: BybitIcon, component: 'exchange' },
      { id: 'coincall', label: 'Coincall', icon: CoincallIcon, component: 'exchange' },
      { id: 'deribit', label: 'Deribit', icon: DeribitIcon, component: 'exchange' },
      { id: 'okx', label: 'OKX', icon: OkxIcon, component: 'exchange' },
      { id: 'otc', label: 'OTC Trader', icon: OtcIcon, component: 'otc' },
    ],
    [],
  );

  return (
    <>
      {generalSettingsSelected.accountListing === 'byExchange' ? (
        <>
          {exchangeDefinitions.map(({ id, label, icon, component }) => {
            const accounts = accountsByExchange[id] ?? [];
            return component === 'exchange' ? (
              <ExchangeAccountList
                key={id}
                accounts={accounts}
                isPortfolioSidebarOpen={isPortfolioSidebarOpen}
                selectedAccountOption={selectedAccountOption}
                handleOptionChange={handleOptionChange}
                isDisabledAccounts={isDisabledAccounts}
                calculatedAccountNames={calculatedAccountNames}
                icon={icon}
                label={label}
                exchange={id}
                isBlurred={isNamesBlurred}
              />
            ) : (
              <OTCAccountList
                key={id}
                accounts={accounts}
                isPortfolioSidebarOpen={isPortfolioSidebarOpen}
                selectedAccountOption={selectedAccountOption}
                handleOptionChange={handleOptionChange}
                isDisabledAccounts={isDisabledAccounts}
                calculatedAccountNames={calculatedAccountNames}
                icon={icon}
                label={label}
                exchange={id}
                isBlurred={isNamesBlurred}
              />
            );
          })}
        </>
      ) : (
        <AlphabeticalAccountList
          accounts={allAccounts}
          isPortfolioSidebarOpen={isPortfolioSidebarOpen}
          selectedAccountOption={selectedAccountOption}
          handleOptionChange={handleOptionChange}
          isDisabledAccounts={isDisabledAccounts}
          calculatedAccountNames={calculatedAccountNames}
          isBlurred={isNamesBlurred}
        />
      )}
    </>
  );
};
